import { useQuery } from '@tanstack/react-query';
import { Button, Modal, Skeleton, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconExternalLink, IconEye, IconLock, IconRefresh } from '@tabler/icons-react';
import { useRidesSecurityService } from '@actschurch/ccms-sdk';
import { ridesKeys } from '@/lib/queryKeys';
import { errorMessage } from '@/lib/copy';
import { isPdfType } from '@/lib/documents';
import { formatShortDate, parseApiDate, stampToDate } from '@/lib/format';
import { useCopy } from '@/lib/settings';
import { Callout } from '@/components/ui';
import { useDocumentViewer } from './DocumentViewer';
import { documentPill } from './DocumentItem';
import { Pill } from './Pill';
import '@/styles/member.css';

interface Props {
  opened: boolean;
  onClose: () => void;
  driverUserId: string;
  driverName: string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const longDate = (v: string | null | undefined) => {
  const d = parseApiDate(v);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '';
};

/**
 * Campus security: a driver's uploaded documents (only when the church lets security see them).
 * The list is fetched when opened and not kept; each "Open" asks the server for a fresh, logged,
 * few-minute link. Photos open in a viewer, PDFs in a new tab.
 */
export function SecurityDocumentsModal({ opened, onClose, driverUserId, driverName }: Props) {
  const { t } = useCopy();
  const security = useRidesSecurityService();
  const viewer = useDocumentViewer((id) => security.getDocumentLink(id));
  const fullScreen = useMediaQuery('(max-width: 480px)') ?? false;
  const docs = useQuery({
    queryKey: ridesKeys.security.documents(driverUserId),
    queryFn: () => security.getUserDocuments(driverUserId),
    enabled: opened && !!driverUserId,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
  const list = (docs.data ?? []).filter((d) => d.hasFile);

  return (
    <>
      <Modal opened={opened} onClose={onClose} title={`Documents for ${driverName}`} centered size="md" fullScreen={fullScreen}>
        <Stack gap="sm">
          <p className="rm-small" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 15 }}>
            <IconLock size={18} aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 2 }} />
            {t('security.documents_note')}
          </p>
          {docs.isLoading && <Skeleton height={90} radius="lg" />}
          {docs.error && (
            <Callout
              tone="bad"
              title="We couldn't load the documents"
              action={<Button variant="default" leftSection={<IconRefresh size={18} />} onClick={() => docs.refetch()}>Try again</Button>}
            >
              {errorMessage(docs.error)}
            </Callout>
          )}
          {docs.data && list.length === 0 && <p className="rm-muted">This driver has no document copies to show.</p>}
          {list.map((d) => {
            const pill = documentPill(d, { approvalRequired: true });
            const uploaded = d.uploadedAt ? stampToDate(d.uploadedAt) : null;
            const pdf = isPdfType(d.contentType);
            return (
              <div key={d.rideUploadID} className="rm-doc" style={{ padding: 12 }}>
                <b style={{ display: 'block', fontSize: 16 }}>{d.uploadTypeName}</b>
                <span className="rm-small" style={{ display: 'block', marginTop: 2 }}>
                  {uploaded ? `Uploaded ${formatShortDate(uploaded)}` : 'Uploaded'}
                  {d.expiresAt ? `. Valid until ${longDate(d.expiresAt)}` : ''}
                </span>
                <div style={{ marginTop: 6 }}><Pill kind={pill.kind}>{pill.label}</Pill></div>
                <Button
                  mt="sm"
                  variant="default"
                  fullWidth
                  leftSection={pdf ? <IconExternalLink size={18} /> : <IconEye size={18} />}
                  loading={viewer.loadingId === d.rideUploadID}
                  onClick={() => viewer.open(d.rideUploadID, `${d.uploadTypeName}, ${driverName}`, d.contentType)}
                >
                  {pdf ? 'Open the PDF' : 'Open the photo'}
                </Button>
              </div>
            );
          })}
          {viewer.error && <Callout tone="bad" live action={<Button variant="default" onClick={viewer.clearError}>OK</Button>}>{viewer.error}</Callout>}
          <Button variant="default" onClick={onClose}>Close</Button>
        </Stack>
      </Modal>
      {viewer.modal}
    </>
  );
}
